import { Component } from '@theme/component';

/**
 * @typedef {object} GroupDrawerRefs
 * @property {HTMLElement} sources
 * @property {HTMLElement & { showDialog?: () => void, closeDialog?: () => void }} dialogController
 */

/**
 * @extends {Component<GroupDrawerRefs>}
 */
class GroupDrawerComponent extends Component {
  requiredRefs = ['sources', 'dialogController'];
  /** @type {HTMLElement | null} */
  #tabListElement = null;
  /** @type {Array<{tabIndex: number, element: HTMLElement}>} */
  #pinnedCtas = [];
  #isDraggingTabs = false;
  #didDragTabs = false;
  #suppressTabClick = false;
  #dragStartX = 0;
  #dragStartScrollLeft = 0;

  connectedCallback() {
    super.connectedCallback();
    // Child custom elements (like dialog-component) may not have populated refs yet
    // at this lifecycle point, so defer initialization to the next frame.
    requestAnimationFrame(() => {
      this.#renderTabs();
      this.#setActiveTab(0);
    });
  }

  /**
   * @param {number} index
   */
  openDrawer(index = 0) {
    this.#setActiveTab(index);
    this.refs.dialogController.showDialog?.();
    requestAnimationFrame(() => {
      for (const slides of this.querySelectorAll('slideshow-slides')) {
        slides.scrollLeft = 0;
      }
    });
  }

  closeDrawer() {
    this.refs.dialogController.closeDialog?.();
  }

  /**
   * @param {number} index
   * @param {Event} event
   */
  handleTriggerClick(index, event) {
    event.preventDefault();
    this.openDrawer(index);
  }

  /**
   * @param {number} index
   * @param {Event} event
   */
  handleTabClick(index, event) {
    if (this.#suppressTabClick) {
      event.preventDefault();
      this.#suppressTabClick = false;
      return;
    }

    event.preventDefault();
    this.#setActiveTab(index);
  }

  #renderTabs() {
    const tabSources = this.refs.sources.querySelectorAll('[data-group-drawer-tab]');
    if (!tabSources.length) return;

    const triggerList = this.refs.dialogController.querySelector('[ref="triggerList"]');
    const tabList = this.refs.dialogController.querySelector('[ref="tabList"]');
    const panelList = this.refs.dialogController.querySelector('[ref="panelList"]');
    if (!(triggerList instanceof HTMLElement)) return;
    if (!(tabList instanceof HTMLElement)) return;
    if (!(panelList instanceof HTMLElement)) return;

    const dialogElement = this.querySelector('dialog[ref="dialog"]');
    const dialogId = dialogElement instanceof HTMLDialogElement ? dialogElement.id : '';

    triggerList.innerHTML = '';
    tabList.innerHTML = '';
    panelList.innerHTML = '';
    this.#pinnedCtas = [];

    for (const [index, source] of tabSources.entries()) {
      const heading = source.getAttribute('data-heading') || '';
      const tabId = `GroupDrawerTab-${this.id}-${index}`;
      const panelId = `GroupDrawerPanel-${this.id}-${index}`;
      const content = source.querySelector('[data-group-drawer-tab-content]');

      const trigger = document.createElement('button');
      trigger.className = 'button-unstyled group-drawer-block__trigger-button';
      trigger.setAttribute('type', 'button');
      trigger.setAttribute('on:click', `group-drawer-component/handleTriggerClick/${index}`);
      if (dialogId) {
        trigger.setAttribute('aria-controls', dialogId);
      }
      const triggerLabel = document.createElement('span');
      triggerLabel.className = 'group-drawer-block__trigger-label';
      triggerLabel.append(document.createTextNode(heading));
      trigger.append(triggerLabel);
      const triggerIcon = document.createElement('span');
      triggerIcon.className = 'group-drawer-block__trigger-icon';
      triggerIcon.setAttribute('aria-hidden', 'true');
      triggerIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
  <g clip-path="url(#clip0_1685_390)">
    <path d="M10 4.58333H5.41667V0H4.58333V4.58333H0V5.41667H4.58333V10H5.41667V5.41667H10V4.58333Z" fill="#6D6D6D"></path>
  </g>
  <defs>
    <clipPath id="clip0_1685_390">
      <rect width="10" height="10" fill="white"></rect>
    </clipPath>
  </defs>
</svg>`;
      trigger.append(triggerIcon);
      triggerList.append(trigger);

      if (!source.hasAttribute('data-hide-tab')) {
        const tabButton = document.createElement('button');
        tabButton.className = 'button-unstyled group-drawer-block__tab-button';
        tabButton.setAttribute('type', 'button');
        tabButton.setAttribute('role', 'tab');
        tabButton.setAttribute('id', tabId);
        tabButton.setAttribute('aria-controls', panelId);
        tabButton.setAttribute('on:click', `group-drawer-component/handleTabClick/${index}`);
        tabButton.textContent = heading;
        tabList.append(tabButton);
      }

      const panel = document.createElement('div');
      panel.className = 'group-drawer-block__panel';
      panel.setAttribute('id', panelId);
      panel.setAttribute('role', 'tabpanel');
      if (!source.hasAttribute('data-hide-tab')) {
        panel.setAttribute('aria-labelledby', tabId);
      }
      panel.hidden = index !== 0;

      if (content instanceof HTMLElement) {
        panel.innerHTML = content.innerHTML;
      }

      panelList.append(panel);

      // Mobile only: move CTA outside the scrollable panelList so it pins to
      // the bottom of the dialog flex column.
      if (window.innerWidth <= 749) {
        for (const cta of panel.querySelectorAll('.group-drawer-cta')) {
          cta.style.display = index !== 0 ? 'none' : '';
          panelList.after(cta);
          this.#pinnedCtas.push({ tabIndex: index, element: cta });
        }
      }
    }

    this.#setupTabListDrag(tabList);
  }

  /**
   * @param {number} index
   */
  #setActiveTab(index) {
    const tabList = this.refs.dialogController.querySelector('[ref="tabList"]');
    const panelList = this.refs.dialogController.querySelector('[ref="panelList"]');
    if (!(tabList instanceof HTMLElement)) return;
    if (!(panelList instanceof HTMLElement)) return;

    const panelId = `GroupDrawerPanel-${this.id}-${index}`;
    const tabButtons = tabList.querySelectorAll('[role="tab"]');
    const panels = panelList.querySelectorAll('[role="tabpanel"]');

    if (!panels.length) return;

    tabList.style.display = [...tabButtons].some(b => b.getAttribute('aria-controls') === panelId) ? '' : 'none';

    for (const button of tabButtons) {
      const isActive = button.getAttribute('aria-controls') === panelId;
      button.setAttribute('aria-selected', `${isActive}`);
      button.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const panel of panels) {
      if (!(panel instanceof HTMLElement)) continue;
      panel.hidden = panel.id !== panelId;
    }

    for (const { tabIndex, element } of this.#pinnedCtas) {
      element.style.display = tabIndex !== index ? 'none' : '';
    }
  }

  /**
   * @param {HTMLElement} tabList
   */
  #setupTabListDrag(tabList) {
    if (this.#tabListElement === tabList) return;

    const previousTabList = this.#tabListElement;
    if (previousTabList) {
      previousTabList.removeEventListener('mousedown', this.#handleTabListDragStart);
    }

    this.#tabListElement = tabList;
    tabList.addEventListener('mousedown', this.#handleTabListDragStart);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleTabListDragStart = (event) => {
    const tabList = this.#tabListElement;
    if (!tabList) return;
    if (event.button !== 0) return;

    this.#isDraggingTabs = true;
    this.#didDragTabs = false;
    this.#dragStartX = event.pageX;
    this.#dragStartScrollLeft = tabList.scrollLeft;

    tabList.classList.add('is-dragging');
    document.addEventListener('mousemove', this.#handleTabListDragMove);
    document.addEventListener('mouseup', this.#handleTabListDragEnd);
  };

  /**
   * @param {MouseEvent} event
   */
  #handleTabListDragMove = (event) => {
    const tabList = this.#tabListElement;
    if (!tabList) return;
    if (!this.#isDraggingTabs) return;

    const deltaX = event.pageX - this.#dragStartX;
    if (Math.abs(deltaX) > 3) {
      this.#didDragTabs = true;
    }

    tabList.scrollLeft = this.#dragStartScrollLeft - deltaX;
    event.preventDefault();
  };

  #handleTabListDragEnd = () => {
    const tabList = this.#tabListElement;
    if (!tabList) return;

    this.#isDraggingTabs = false;
    tabList.classList.remove('is-dragging');
    document.removeEventListener('mousemove', this.#handleTabListDragMove);
    document.removeEventListener('mouseup', this.#handleTabListDragEnd);

    if (this.#didDragTabs) {
      this.#suppressTabClick = true;
    }
  };
}

if (!customElements.get('group-drawer-component')) {
  customElements.define('group-drawer-component', GroupDrawerComponent);
}
